import React, { useEffect, useState } from "react";
import { GroupE, GroupT } from "shared/routes";
import SidebarLink from "./SidebarLink";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Center,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useDeleteService, useGetServices } from "shared/api/requests/services";
import Loader from "shared/components/Loader";
import useGetColors from "shared/hooks/useGetColors";
import { DTO } from "shared/api/types/services";
import DeleteModal from "shared/views/admin/DeleteModal";
import { useAuthContext } from "shared/contexts/AuthContext";
import { RoleE } from "shared/api/types/auth";
import MockAiText from "./MockAiText";
import MuiIcon from "components/MuiIcon";
import { truncateString } from "shared/utils/formattings";
import { useAccordionData } from "../lib/useAccordionData";
import { useLocation } from "react-router-dom";


interface Props {
  groups: GroupT[];
}

export function SidebarLinks({ groups }: Props) {
  const { self } = useAuthContext();
  const colors = useGetColors();

  const [isOpen, setIsOpen] = useState(false);
  const [serviceNameToDelete, setServiceNameToDelete] = useState<string | null>(null);
  const [isOpenConfirmationDelete, setIsOpenConfirmationDelete] = useState(false);
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);

  const { mutate: deleteServiceMutate } = useDeleteService();
  const { data, isLoading } = useGetServices({});
  const services = (data?.pages.flat() ?? []) as unknown as DTO[];

  const accordionData = useAccordionData({ groups, services });

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const handleDelete = () => {
    if (!serviceNameToDelete) return;
    const serviceId = services?.find((item) => item.name === serviceNameToDelete)?.id;
    if (serviceId) deleteServiceMutate({ serviceId });
    setIsOpenConfirmationDelete(false);
  };

  const onAccordionChange = (index: number[] | number) => {
    setExpandedIndexes(Array.isArray(index) ? index : [index])
  };

  const location = useLocation();

  useEffect(() => {
    if (expandedIndexes.length > 0) return;
    const [_, __, serviceName, serviceId] = location.pathname.split('/');
      const serviceIndex = accordionData.findIndex((item) => {
        return item.group.routes.find((route) => {
          const [sName, sId] = route.path.split('/');
          if (serviceId) return sId === serviceId;
          return sName === serviceName
        });
      });
      if (serviceIndex != -1) setExpandedIndexes([serviceIndex])
  }, [location, expandedIndexes, accordionData]);


  if (isLoading || !data) return <Loader />;

  return (
    <>
      <Accordion
        allowMultiple
        onChange={onAccordionChange}
        index={expandedIndexes}
        display='flex'
        flexDirection='column'
        gap={2}
      >
        {accordionData.map(({ group, serviceName, mock }, idx) => {
          return (
            <AccordionItem
              key={`${group.group}-${serviceName}-${idx}`}
              borderTop={`1px solid ${colors.gray200}`}
              borderBottom={`1px solid ${colors.gray200}`}
            >
              <AccordionButton onClick={mock && onOpen}>
                <Flex w="100%" alignItems="center">
                  <Center me="16px" color="brand.500">{group.icon}</Center>
                  <Tooltip label={serviceName} openDelay={200}>
                    <Text fontWeight="700">
                      {group.group}{serviceName && `– ${truncateString(serviceName)}`}
                    </Text>
                  </Tooltip>
                </Flex>

                <AccordionIcon />
                {group.group === GroupE.AI && self.role === RoleE.ADMIN && (
                  <IconButton
                    size="sm"
                    aria-label={"remove-service"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpenConfirmationDelete(true);
                      setServiceNameToDelete(serviceName);
                    }}
                  >
                    <MuiIcon size={20} name="delete" color={colors.red500} />
                  </IconButton>
                )}
                <DeleteModal
                  title={"Are you sure?"}
                  text={""}
                  onClose={() => setIsOpenConfirmationDelete(false)}
                  isOpen={isOpenConfirmationDelete}
                  onSubmit={handleDelete}
                />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {group.routes.map((route, index) => (
                  <SidebarLink key={index} route={route} />
                ))}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>

      <MockAiText isOpen={isOpen} onClose={onClose} />
    </>
  );
}

export default SidebarLinks;
